/**
 * Wire types for the Morning List page (/wa-briefing).
 *
 * Mirror of dream-types/api/wa-briefing.ts. Import from '@dreamai/types' and
 * delete this file once that version of the package is released.
 */

/** What tapping an option does: a deterministic change now, assistant work queued for after the list, or a topic parked to talk about. */
export type WaBriefingOptionEffect = 'commit' | 'intent' | 'discuss';

/** Ids of the standing options every card carries, after its generated ones. */
export const WA_BRIEFING_DISCUSS_OPTION_ID = 'discuss';
export const WA_BRIEFING_DEALT_WITH_OPTION_ID = 'dealt_with';
export const WA_BRIEFING_NOT_RELEVANT_OPTION_ID = 'not_relevant';
/** On an owed / awaiting reply (and a to-do, as `task:moved`): not this morning; the assistant picks the day. */
/** On a to-do card: hand it to the assistant, with a typed or spoken note as the brief. */
export const WA_BRIEFING_TASK_DELEGATE_OPTION_ID = 'task:delegate';
/** Page-only entry on a to-do's card menu: find it a time today (find-time → book), then keep it. */
export const WA_BRIEFING_TASK_FIND_TIME_OPTION_ID = 'task:find_time';
export const WA_BRIEFING_TASK_KEPT_OPTION_ID = 'task:kept';

export interface WaBriefingOption {
  id: string;
  label: string;
  effect: WaBriefingOptionEffect;
  /** True for the composer's recommended option (rendered as the main action). */
  recommended: boolean;
  /** True for the secondary options (discuss / dealt with / not relevant, drop on a to-do): behind the main buttons. */
  standing: boolean;
}

export interface WaBriefingDecision {
  optionId: string;
  label: string;
  effect: WaBriefingOptionEffect;
  note: string | null;
  /** True when the decision already changed something (a to-do or Key Date was written) and cannot be undone from the page. */
  locked: boolean;
}

export type WaBriefingItemStatus = 'open' | 'closed';

/** A page the card opens in a new tab: the draft the assistant wrote in the user's own mailbox, for them to send. */
export interface WaBriefingLink {
  url: string;
  kind: 'draft' | 'promotion' | 'linkedin_draft' | 'whatsapp_draft';
  label?: string | null;
  draftText?: string | null;
  delivery?: 'in_app' | 'pending' | 'card_only' | null;
  meetingDecision?: 'pending' | null;
}

export interface WaBriefingItem {
  id: string;
  /** Short lead line ("Charlie's waiting for a GiveWheel update."). */
  headline: string;
  /** One line of context under the headline. */
  context: string;
  /** Source label shown as the card's eyebrow ("Email · Charlie Rose"). */
  sourceLabel: string | null;
  isPromotion?: boolean;
  /** Who the person is when the headline alone may not say ("Matt · matt@…"). */
  whoLabel?: string | null;
  /** For a single to-do's card: the to-do, so the page can find it a time (as the to-do view does). */
  todoId: string | null;
  /** Rendered as the card's "Open draft" button; null for most cards. */
  link: WaBriefingLink | null;
  /** Promotion artwork, shown above the headline; null for most cards. */
  image: { url: string } | null;
  status: WaBriefingItemStatus;
  options: WaBriefingOption[];
  decision: WaBriefingDecision | null;
}

export type WaBriefingTodoDay = 'today' | 'tomorrow';

export interface WaBriefingTodo {
  id: string;
  title: string;
  /** YYYY-MM-DD in the user's calendar, or null for undated. */
  dueDate: string | null;
  /** True when this to-do was added from the Morning List this morning. */
  addedThisMorning: boolean;
  /** Ticked off this morning (kept on the list, struck through). */
  done: boolean;
  /** The list it sits on for this user; null = no list (Today / Later). */
  listId: string | null;
  /** Given to this user by someone else. */
  from: WaBriefingPerson | null;
  /** This user gave it to someone else (Assigned to others). */
  assignee: WaBriefingPerson | null;
  /** When this user last asked the assignee's assistant whether it is done. */
  askedAt: string | null;
  /** Time booked for it in the calendar from the page, when there is one. */
  scheduled: WaBriefingSlot | null;
  /** The assistant said this morning it can do this: the page offers Delegate. */
  canDelegate: boolean;
  /** Handed to the assistant from the page (starts with the rest of the list's chosen work). */
  delegation: WaBriefingDelegation | null;
  /** The assistant already has this in hand from elsewhere (asked in chat, work under way): not the user's to pick up. */
  inHand: WaBriefingInHand | null;
  /** Live state of the assistant's task thread on it, when newer than this morning's note. */
  work?: WaBriefingWork | null;
}

export interface WaBriefingWork {
  state:
    | 'working'
    | 'waiting'
    | 'waiting_others'
    | 'done'
    | 'failed'
    | 'stopped';
  /** The thread's latest one-line summary. */
  detail: string | null;
  /** ISO instant of the thread's last update. */
  at: string;
}

export interface WaBriefingPerson {
  userId: string;
  name: string;
}

/** Someone a to-do can be given to. */
export interface WaBriefingAssignable extends WaBriefingPerson {
  group: 'circle' | 'organisation';
  detail: string | null;
}

/** A named list the page can filter by. Today and Later are implicit. */
export interface WaBriefingList {
  id: string;
  name: string;
}

export interface WaBriefingSlot {
  /** ISO instants. */
  start: string;
  end: string;
  /** HH:mm in the user's timezone. */
  time: string;
}

export interface WaBriefingDelegation {
  note: string | null;
  /** True once the assistant has been handed it. */
  started: boolean;
}

export interface WaBriefingInHand {
  /** What the assistant said this morning it is doing about it, first person; null when it gave no line. */
  what: string | null;
}

/** Where an email suggestion goes when added. */
export type WaBriefingSuggestionTarget = 'todo' | 'calendar';

/** Something from the user's email that could go on their list or calendar. */
export interface WaBriefingSuggestion {
  id: string;
  title: string;
  /** One line: who it is from and why it matters. */
  detail: string;
  target: WaBriefingSuggestionTarget;
  /** YYYY-MM-DD the email gave (deadline or appointment day), or null. */
  dueOn: string | null;
  /** HH:mm when the email gave a time. */
  dueTime: string | null;
  /** YYYY-MM-DD the to-do is put on when added: worked back from the deadline. */
  doOn: string | null;
}

export interface WaBriefingPlanIntent {
  itemId: string;
  title: string;
  /** What the assistant will do, in one line. */
  intent: string;
  /** True once the assistant has started on it. */
  started: boolean;
}

export interface WaBriefingPlan {
  /** Assistant work queued for after the list; starts only when the user says go on WhatsApp. */
  intents: WaBriefingPlanIntent[];
  /** Topics parked to discuss with the assistant. */
  discuss: Array<{ itemId: string; title: string }>;
}

// ── GET /wa-briefing/content?token=<uuid> ───────────────────────────────

export interface WaBriefingContentData {
  assistantName: string | null;
  /** The user's first name, for the greeting. */
  firstName: string | null;
  /** BCP-47 language code of the user's preferred language. */
  language: string;
  /** YYYY-MM-DD of the briefing in the user's calendar. */
  date: string;
  timezone: string;
  items: WaBriefingItem[];
  todos: WaBriefingTodo[];
  lists: WaBriefingList[];
  suggestions: WaBriefingSuggestion[];
  plan: WaBriefingPlan;
  /** True once the user has reached the end of the list and finished. */
  finished: boolean;
  /** Deep link back to the WhatsApp conversation with the assistant. */
  whatsappUrl: string | null;
  /** The feature tour to play after the cards, for people who joined before the new onboarding; null for everyone else. */
  tour?: WaBriefingTour | null;
  /** A staff test of one promotion card: taps are not saved. */
  test?: boolean;
}

/** A tour scene the user asked to hear more about ("Show me after"). */
export interface WaBriefingTourTopic {
  /** Scene id (scheduling, assistants, shop, email, check_in, organisations). */
  id: string;
  /** How the hand-off names it: "booking meetings". */
  title: string;
  /** The Today to-do added for it; ticking it off drops the topic from the hand-off. */
  todoId?: string | null;
}

export interface WaBriefingTour {
  /** Which scene ends the tour: organisations (connected work email) or the 7pm check-in. */
  ending: 'check_in' | 'organisations';
  topics: WaBriefingTourTopic[];
  /** True once the user reached the end of the tour. */
  finished: boolean;
}

// ── POST /wa-briefing/tour · POST /wa-tour/topics ───────────────────────

export interface WaBriefingTourRequest {
  token: string;
  /** Every topic queued so far (the page sends the full set each time). */
  topics: Array<{ id: string; title: string }>;
  /** True when the user reached the end of the tour. */
  finished?: boolean;
}

export interface WaBriefingTourResponse {
  success: boolean;
  message: string;
  data?: { tour: WaBriefingTour; todos: WaBriefingTodo[] };
  error?: string;
}

// ── GET /wa-tour/content?token=<uuid> · POST /wa-tour/finish ───────────

export interface WaTourContentData {
  assistantName: string | null;
  firstName: string | null;
  language: string;
  whatsappUrl: string | null;
  tour: WaBriefingTour;
}

export interface WaTourContentResponse {
  success: boolean;
  message: string;
  data?: WaTourContentData;
  error?: string;
}

export interface WaTourFinishResponse {
  success: boolean;
  message: string;
  data?: { handedOff: boolean; whatsappUrl: string | null };
  error?: string;
}

export interface WaBriefingContentResponse {
  success: boolean;
  message: string;
  data?: WaBriefingContentData;
  error?: string;
}

// ── POST /wa-briefing/decide ────────────────────────────────────────────

export interface WaBriefingDecideRequest {
  token: string;
  itemId: string;
  optionId: string;
  /** Free text the user added with the choice (e.g. why something is not relevant). */
  note?: string | null;
}

export interface WaBriefingDecideData {
  item: WaBriefingItem;
  /** Human-readable confirmation of what a commit changed, when it did. */
  applied: string | null;
  /** True when this shape has been dismissed before without a reason: worth asking why (POST /why). */
  askWhy: boolean;
  todos: WaBriefingTodo[];
  plan: WaBriefingPlan;
}

export interface WaBriefingDecideResponse {
  success: boolean;
  message: string;
  data?: WaBriefingDecideData;
  error?: string;
}

// ── POST /wa-briefing/why ───────────────────────────────────────────────

/** The user's reason for a "not relevant", given after the fact. A learning signal, never a rule. */
export interface WaBriefingWhyRequest {
  token: string;
  itemId: string;
  reason: string;
}

export interface WaBriefingWhyResponse {
  success: boolean;
  message: string;
  error?: string;
}

// ── POST /wa-briefing/reopen ────────────────────────────────────────────

export interface WaBriefingReopenRequest {
  token: string;
  itemId: string;
}

export interface WaBriefingReopenResponse {
  success: boolean;
  message: string;
  data?: { item: WaBriefingItem; plan: WaBriefingPlan };
  error?: string;
}

// ── POST /wa-briefing/finish ────────────────────────────────────────────

export interface WaBriefingFinishRequest {
  token: string;
}

export interface WaBriefingFinishData {
  todos: WaBriefingTodo[];
  plan: WaBriefingPlan;
  /** True when the plan (queued work + topics) was handed to the assistant to put to the user on WhatsApp. */
  planHandedOff: boolean;
  whatsappUrl: string | null;
}

export interface WaBriefingFinishResponse {
  success: boolean;
  message: string;
  data?: WaBriefingFinishData;
  error?: string;
}

// ── POST /wa-briefing/todo ──────────────────────────────────────────────

export interface WaBriefingTodoRequest {
  token: string;
  todoId: string;
  /** Tick / untick. */
  done?: boolean;
  /** Move to today / tomorrow (in the user's calendar). */
  day?: WaBriefingTodoDay;
  /** Move to this date (YYYY-MM-DD); wins over `day`. null = no date. */
  dueDate?: string | null;
  /** Put it on this list for this user only; null = no list. */
  listId?: string | null;
  /** New wording (only the person who added it). */
  title?: string;
}

// ── POST /wa-briefing/add, /list, /assign, /ask, /people ───────────────

export interface WaBriefingAddRequest {
  token: string;
  title: string;
  listId?: string | null;
  dueDate?: string | null;
}

/** POST /morning-list/list/rename and /list/delete (owner only). */
export interface WaBriefingListChangeRequest {
  token?: string;
  listId: string;
  /** Rename only. */
  name?: string;
}

export interface WaBriefingListRequest {
  token: string;
  name: string;
}

export interface WaBriefingListResponse {
  success: boolean;
  message: string;
  data?: { lists: WaBriefingList[]; listId: string };
  error?: string;
}

export interface WaBriefingAssignRequest {
  token: string;
  todoId: string;
  /** null = take it back. */
  assigneeUserId: string | null;
}

export interface WaBriefingAskRequest {
  token: string;
  todoId: string;
  note?: string | null;
}

export interface WaBriefingPeopleResponse {
  success: boolean;
  message: string;
  data?: { people: WaBriefingAssignable[] };
  error?: string;
}

export interface WaBriefingTodoResponse {
  success: boolean;
  message: string;
  data?: { todos: WaBriefingTodo[] };
  error?: string;
}

// ── POST /wa-briefing/suggestion ────────────────────────────────────────

export type WaBriefingSuggestionAction = 'add' | 'not_now';

export interface WaBriefingSuggestionRequest {
  token: string;
  suggestionId: string;
  action: WaBriefingSuggestionAction;
}

export interface WaBriefingSuggestionResponse {
  success: boolean;
  message: string;
  data?: {
    todos: WaBriefingTodo[];
    suggestions: WaBriefingSuggestion[];
    /** The calendar slot booked, when the suggestion was an appointment. */
    booked: WaBriefingSlot | null;
  };
  error?: string;
}

// ── POST /wa-briefing/find-time ─────────────────────────────────────────

export interface WaBriefingFindTimeRequest {
  token: string;
  todoId: string;
  /** Starts already offered and turned down ("Another time"). */
  skip?: string[];
  /** Look only in this part of the day. */
  part?: WaBriefingDayPart;
  /**
   * The start currently offered in `part`: the first slot of the next free
   * stretch after it comes back, round to the part's first again at the end.
   */
  after?: string;
}

/** The parts of the day Find time can be pointed at. */
export type WaBriefingDayPart =
  | 'morning'
  | 'after_lunch'
  | 'afternoon'
  | 'end_of_day';

export interface WaBriefingTimeOption {
  slot: WaBriefingSlot;
  context: string | null;
}

export interface WaBriefingFindTimeData {
  slot: WaBriefingSlot | null;
  /** How long the slot is sized for. */
  minutes: number;
  /** Nearby calendar context ("after Standup"), or null. */
  context: string | null;
  /** Why there is no slot, when there is none. */
  reason: 'no_calendar' | 'calendar_unavailable' | 'no_free_time' | null;
  /** The parts of the day that still have room for it. */
  parts: WaBriefingDayPart[];
  /** Other free starts to pick from (same part of the day as `slot`), in order. */
  options?: WaBriefingTimeOption[];
}

export interface WaBriefingFindTimeResponse {
  success: boolean;
  message: string;
  data?: WaBriefingFindTimeData;
  error?: string;
}

// ── POST /wa-briefing/book ──────────────────────────────────────────────

export interface WaBriefingBookRequest {
  token: string;
  todoId: string;
  /** ISO start of the slot offered by find-time. */
  start: string;
}

export interface WaBriefingBookResponse {
  success: boolean;
  message: string;
  data?: { todos: WaBriefingTodo[] };
  error?: string;
}

// ── POST /wa-briefing/delegate ──────────────────────────────────────────

export interface WaBriefingDelegateRequest {
  token: string;
  todoId: string;
  /** What the user added, typed or transcribed; empty = "just handle it". */
  note?: string | null;
}

export interface WaBriefingDelegateResponse {
  success: boolean;
  message: string;
  data?: { todos: WaBriefingTodo[] };
  error?: string;
}

// ── POST /wa-briefing/transcribe (multipart: token, audio) ──────────────

export interface WaBriefingTranscribeResponse {
  success: boolean;
  message: string;
  data?: { text: string };
  error?: string;
}
